import { ApiError, PaginatedResponseDto } from "@/client/extend";
import {
  DataTableContext,
  PaginationRequest,
  useTableCurrentItemRef,
} from "./data-table-context";
import { QueryKey } from "@tanstack/react-query";
import { DataTable, DataTableProps } from "./data-table";

export type DataTableAssignProps<
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends PaginatedResponseDto<TRow> = PaginatedResponseDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
> = {
  context: DataTableContext<
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey
  >;
} & Omit<
  DataTableProps<TRow, any>,
  "table" | "columns" | "box" | "total" | "loading" | "refresh" | "itemType"
>;

export type DataTableAssignPropsInfer<Context extends DataTableContext> =
  Context extends DataTableContext<
    infer TRow,
    infer TArgument,
    infer TQueryFnData,
    infer TError,
    infer TData,
    infer TQueryKey
  >
    ? DataTableAssignProps<
        TRow,
        TArgument,
        TQueryFnData,
        TError,
        TData,
        TQueryKey
      >
    : never;

export function DataTableAssign<
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends PaginatedResponseDto<TRow> = PaginatedResponseDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
>({
  context,
  ...props
}: DataTableAssignProps<
  TRow,
  TArgument,
  TQueryFnData,
  TError,
  TData,
  TQueryKey
>) {
  const queryResult = context.useTableQueryResult();
  return (
    <DataTable
      {...props}
      table={context.useTable()}
      columns={context.useTableColumns()}
      total={context.useTableTotal()}
      count={context.useTableCount()}
      loading={queryResult.isFetching}
      refresh={context.useTableRefresh()}
      itemType={context.useTableItemType()}
      gallery={context.useTableGallery()}
      box={context.useTableBox()}
      currentRef={useTableCurrentItemRef()}
    />
  );
}
